import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Form, Message, Loader, Header, Segment, Icon, Label, Pagination, Menu, Dropdown, Input, Checkbox
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useToast } from '../../hooks/useToast';
import BaseModal, { BaseModalAction } from '../../components/shared/BaseModal/BaseModal';
import { accessSettingsService, AccessSettingDto, CreateAccessSettingDto } from '../../services/accessSettings/AccessSettingsService';
import { loginAuditService, LoginAuditDto, LoginAuditPagedResponse } from '../../services/loginAudits/LoginAuditService';
import {
  fastchipClusterSettingsService,
  FastchipClusterSettingsDto,
} from '../../services/fastchipClusterSettings/FastchipClusterSettingsService';
import { setFastchipClusterSettings } from '../../utils/fastchipClusterRules';

const DAY_NAMES: Record<number, string> = {
  0: 'Domingo', 1: 'Segunda', 2: 'Terça', 3: 'Quarta', 4: 'Quinta', 5: 'Sexta', 6: 'Sábado'
};

const AccessManagementPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<'rules' | 'logins' | 'clusters'>('rules');

  // Fastchip Cluster Settings state (E.R.2 / DH — mercado RJ/MG/ES)
  const [clusterSettings, setClusterSettings] = useState<FastchipClusterSettingsDto | null>(null);
  const [clusterSettingsLoading, setClusterSettingsLoading] = useState(false);
  const [clusterSettingsSaving, setClusterSettingsSaving] = useState(false);

  // Access Settings state
  const [settings, setSettings] = useState<AccessSettingDto[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState<AccessSettingDto | null>(null);
  const [formData, setFormData] = useState<CreateAccessSettingDto>({
    roleName: null,
    startTime: '08:00',
    endTime: '19:30',
    allowedDays: '1,2,3,4,5',
    isActive: true,
    description: null,
  });

  // Login Audits state
  const [audits, setAudits] = useState<LoginAuditPagedResponse | null>(null);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditFilters, setAuditFilters] = useState<{ username?: string; success?: boolean }>({});

  const loadSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const data = await accessSettingsService.getAll();
      setSettings(data);
    } catch {
      showError('Erro ao carregar regras de acesso');
    } finally {
      setSettingsLoading(false);
    }
  }, [showError]);

  const loadAudits = useCallback(async (page: number = 1) => {
    try {
      setAuditsLoading(true);
      const data = await loginAuditService.getLogins(page, 20, {
        username: auditFilters.username,
        success: auditFilters.success,
      });
      setAudits(data);
    } catch {
      showError('Erro ao carregar histórico de logins');
    } finally {
      setAuditsLoading(false);
    }
  }, [auditFilters, showError]);

  useEffect(() => {
    if (activeTab === 'rules') loadSettings();
  }, [activeTab, loadSettings]);

  useEffect(() => {
    if (activeTab === 'logins') loadAudits(auditPage);
  }, [activeTab, auditPage, loadAudits]);

  const loadClusterSettings = useCallback(async () => {
    try {
      setClusterSettingsLoading(true);
      const data = await fastchipClusterSettingsService.getSettings();
      setClusterSettings(data);
    } catch {
      showError('Erro ao carregar configuração de clusters Fastchip');
    } finally {
      setClusterSettingsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (activeTab === 'clusters') loadClusterSettings();
  }, [activeTab, loadClusterSettings]);

  const handleSaveClusterSettings = async () => {
    if (!clusterSettings) return;
    try {
      setClusterSettingsSaving(true);
      const updated = await fastchipClusterSettingsService.updateSettings(clusterSettings);
      setClusterSettings(updated);
      setFastchipClusterSettings(updated);
      showSuccess('Configuração de clusters Fastchip atualizada');
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar configuração de clusters');
    } finally {
      setClusterSettingsSaving(false);
    }
  };

  const handleCreateSetting = () => {
    setEditingSetting(null);
    setFormData({ roleName: null, startTime: '08:00', endTime: '19:30', allowedDays: '1,2,3,4,5', isActive: true, description: null });
    setShowModal(true);
  };

  const handleEditSetting = (setting: AccessSettingDto) => {
    setEditingSetting(setting);
    setFormData({
      roleName: setting.roleName,
      startTime: setting.startTime,
      endTime: setting.endTime,
      allowedDays: setting.allowedDays,
      isActive: setting.isActive,
      description: setting.description,
    });
    setShowModal(true);
  };

  const handleSubmitSetting = async () => {
    try {
      if (editingSetting) {
        await accessSettingsService.update(editingSetting.id, formData);
        showSuccess('Regra atualizada com sucesso');
      } else {
        await accessSettingsService.create(formData);
        showSuccess('Regra criada com sucesso');
      }
      setShowModal(false);
      loadSettings();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar regra');
    }
  };

  const handleDeleteSetting = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja remover esta regra?')) return;
    try {
      await accessSettingsService.delete(id);
      showSuccess('Regra removida');
      loadSettings();
    } catch {
      showError('Erro ao remover regra');
    }
  };

  const toggleDay = (day: number) => {
    const currentDays = formData.allowedDays?.split(',').filter(d => d).map(Number) || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort();
    setFormData({ ...formData, allowedDays: newDays.join(',') });
  };

  const formatDays = (days: string) => {
    return days.split(',').filter(d => d).map(d => DAY_NAMES[parseInt(d)]?.substring(0, 3) || d).join(', ');
  };

  const formatUserAgent = (ua: string | null) => {
    if (!ua) return '-';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return ua.substring(0, 30) + '...';
  };

  const modalActions: BaseModalAction[] = [
    { key: 'cancel', content: 'Cancelar', onClick: () => setShowModal(false) },
    { key: 'submit', content: editingSetting ? 'Atualizar' : 'Criar', onClick: handleSubmitSetting, primary: true },
  ];

  return (
    <GlassPageWrapper>
      <Header as="h2" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="lock" />
        <Header.Content>
          Gestão de Acessos
          <Header.Subheader>Gerencie horários de acesso e visualize histórico de logins</Header.Subheader>
        </Header.Content>
      </Header>

      <Menu pointing secondary style={{ marginBottom: '20px' }}>
        <Menu.Item
          name="Regras de Acesso"
          icon="clock"
          active={activeTab === 'rules'}
          onClick={() => setActiveTab('rules')}
        />
        <Menu.Item
          name="Histórico de Logins"
          icon="history"
          active={activeTab === 'logins'}
          onClick={() => setActiveTab('logins')}
        />
        <Menu.Item
          name="Clusters Fastchip"
          icon="sitemap"
          active={activeTab === 'clusters'}
          onClick={() => setActiveTab('clusters')}
        />
      </Menu>

      {activeTab === 'rules' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <p style={{ color: '#666', margin: 0 }}>
              Configure horários e dias permitidos de acesso ao sistema por perfil. Admin sempre tem acesso irrestrito.
            </p>
            <Button primary icon="plus" content="Nova Regra" onClick={handleCreateSetting} />
          </div>

          {settingsLoading ? (
            <Segment><Loader active inline="centered">Carregando...</Loader></Segment>
          ) : settings.length === 0 ? (
            <Message info>
              <Message.Header>Nenhuma regra configurada</Message.Header>
              <p>O sistema usará o horário padrão: 08:00 às 19:30, segunda a sexta.</p>
            </Message>
          ) : (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Perfil</Table.HeaderCell>
                  <Table.HeaderCell>Horário Início</Table.HeaderCell>
                  <Table.HeaderCell>Horário Fim</Table.HeaderCell>
                  <Table.HeaderCell>Dias Permitidos</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Descrição</Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {settings.map(s => (
                  <Table.Row key={s.id}>
                    <Table.Cell>
                      {s.roleName ? (
                        <Label color="blue">{s.roleName}</Label>
                      ) : (
                        <Label color="purple"><Icon name="globe" /> Padrão (todos)</Label>
                      )}
                    </Table.Cell>
                    <Table.Cell><strong>{s.startTime}</strong></Table.Cell>
                    <Table.Cell><strong>{s.endTime}</strong></Table.Cell>
                    <Table.Cell>{formatDays(s.allowedDays)}</Table.Cell>
                    <Table.Cell>
                      <Label color={s.isActive ? 'green' : 'grey'}>
                        {s.isActive ? 'Ativa' : 'Inativa'}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>{s.description || '-'}</Table.Cell>
                    <Table.Cell textAlign="center">
                      <Button icon="edit" size="small" title="Editar" onClick={() => handleEditSetting(s)} />
                      <Button icon="trash" size="small" color="red" title="Remover" onClick={() => handleDeleteSetting(s.id)} />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      )}

      {activeTab === 'logins' && (
        <div>
          <Segment style={{ marginBottom: '15px' }}>
            <Form>
              <Form.Group inline>
                <Form.Field>
                  <label>Usuário</label>
                  <Input
                    placeholder="Filtrar por usuário..."
                    value={auditFilters.username || ''}
                    onChange={(_, { value }) => setAuditFilters({ ...auditFilters, username: value })}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Status</label>
                  <Dropdown
                    placeholder="Todos"
                    selection
                    clearable
                    options={[
                      { key: 'all', text: 'Todos', value: '' },
                      { key: 'success', text: 'Sucesso', value: 'true' },
                      { key: 'failed', text: 'Falha', value: 'false' },
                    ]}
                    value={auditFilters.success === undefined ? '' : String(auditFilters.success)}
                    onChange={(_, { value }) =>
                      setAuditFilters({ ...auditFilters, success: value === '' ? undefined : value === 'true' })
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <label>&nbsp;</label>
                  <Button icon="search" content="Buscar" onClick={() => { setAuditPage(1); loadAudits(1); }} />
                </Form.Field>
              </Form.Group>
            </Form>
          </Segment>

          {auditsLoading ? (
            <Segment><Loader active inline="centered">Carregando...</Loader></Segment>
          ) : !audits || audits.items.length === 0 ? (
            <Message info>
              <Message.Header>Nenhum login registrado</Message.Header>
              <p>O histórico de logins aparecerá aqui após os primeiros acessos.</p>
            </Message>
          ) : (
            <>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Usuário</Table.HeaderCell>
                    <Table.HeaderCell>IP</Table.HeaderCell>
                    <Table.HeaderCell>Localização</Table.HeaderCell>
                    <Table.HeaderCell>Navegador</Table.HeaderCell>
                    <Table.HeaderCell>Data/Hora</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Motivo Falha</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {audits.items.map((audit: LoginAuditDto) => (
                    <Table.Row key={audit.id} negative={!audit.success}>
                      <Table.Cell><strong>{audit.username || '-'}</strong></Table.Cell>
                      <Table.Cell style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{audit.ipAddress || '-'}</Table.Cell>
                      <Table.Cell>
                        {audit.city || audit.state ? (
                          <Label size="small">
                            <Icon name="map marker alternate" />
                            {[audit.city, audit.state].filter(Boolean).join(', ')}
                            {audit.country && ` - ${audit.country}`}
                          </Label>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>{formatUserAgent(audit.userAgent)}</Table.Cell>
                      <Table.Cell>{new Date(audit.createdAt).toLocaleString('pt-BR')}</Table.Cell>
                      <Table.Cell>
                        <Label color={audit.success ? 'green' : 'red'} size="small">
                          <Icon name={audit.success ? 'check' : 'times'} />
                          {audit.success ? 'Sucesso' : 'Falha'}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>{audit.failureReason || '-'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Footer>
                  <Table.Row>
                    <Table.HeaderCell colSpan="7">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Mostrando {audits.items.length} de {audits.totalCount} registros</span>
                        {audits.totalPages > 1 && (
                          <Pagination
                            activePage={auditPage}
                            totalPages={audits.totalPages}
                            onPageChange={(_, { activePage }) => setAuditPage(activePage as number)}
                            size="small"
                          />
                        )}
                      </div>
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Footer>
              </Table>
            </>
          )}
        </div>
      )}

      {activeTab === 'clusters' && (
        <div>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            E.R.2 atende São Paulo (DDD 11–19). DH atende RJ/MG/ES. Os dois podem ficar ligados:
            contratos novos de SP usam E.R.2; RJ/MG/ES usam DH. Não desligue E.R.2 (SP precisa dele).
          </p>

          {clusterSettingsLoading || !clusterSettings ? (
            <Segment><Loader active inline="centered">Carregando...</Loader></Segment>
          ) : (
            <Segment>
              <Form>
                <Form.Field>
                  <Checkbox
                    toggle
                    label="Habilitar cluster E.R.2 (SP)"
                    checked={clusterSettings.er2Enabled}
                    onChange={(_, { checked }) =>
                      setClusterSettings({ ...clusterSettings, er2Enabled: checked ?? false })
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    toggle
                    label="Habilitar cluster DH (RJ/MG/ES)"
                    checked={clusterSettings.dhEnabled}
                    onChange={(_, { checked }) =>
                      setClusterSettings({ ...clusterSettings, dhEnabled: checked ?? false })
                    }
                  />
                </Form.Field>
                {clusterSettings.er2Enabled && clusterSettings.dhEnabled && (
                  <Form.Field>
                    <label>Cluster padrão (times sem preferência configurada)</label>
                    <Dropdown
                      selection
                      options={[
                        { key: 'ER2', text: 'E.R.2', value: 'ER2' },
                        { key: 'DH', text: 'DH', value: 'DH' },
                      ]}
                      value={clusterSettings.defaultCluster?.trim().toUpperCase().includes('DH') ? 'DH' : 'ER2'}
                      onChange={(_, { value }) =>
                        setClusterSettings({ ...clusterSettings, defaultCluster: value as string })
                      }
                    />
                  </Form.Field>
                )}
                {!clusterSettings.er2Enabled && !clusterSettings.dhEnabled && (
                  <Message warning>
                    Nenhum cluster habilitado — o sistema usará DH como fallback de emergência.
                    Habilite ao menos um cluster.
                  </Message>
                )}
              </Form>
              <Button
                primary
                content="Salvar"
                loading={clusterSettingsSaving}
                disabled={clusterSettingsSaving}
                onClick={handleSaveClusterSettings}
                style={{ marginTop: '15px' }}
              />
            </Segment>
          )}
        </div>
      )}

      <BaseModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingSetting ? 'Editar Regra de Acesso' : 'Nova Regra de Acesso'}
        actions={modalActions}
        size="small"
      >
        <Form>
          <Form.Field>
            <label>Perfil (deixe vazio para regra padrão)</label>
            <Dropdown
              placeholder="Padrão (todos os perfis)"
              selection
              clearable
              options={[
                { key: 'default', text: 'Padrão (todos)', value: '' },
                { key: 'Seller', text: 'Seller', value: 'Seller' },
                { key: 'Validator', text: 'Validator', value: 'Validator' },
                { key: 'TeamLeader', text: 'TeamLeader', value: 'TeamLeader' },
                { key: 'Manager', text: 'Manager', value: 'Manager' },
                { key: 'BKO', text: 'BKO', value: 'BKO' },
                { key: 'Tester', text: 'Tester', value: 'Tester' },
              ]}
              value={formData.roleName || ''}
              onChange={(_, { value }) => setFormData({ ...formData, roleName: value === '' ? null : value as string })}
            />
          </Form.Field>
          <Form.Group widths="equal">
            <Form.Field required>
              <label>Horário Início</label>
              <Input
                type="time"
                value={formData.startTime}
                onChange={(_, { value }) => setFormData({ ...formData, startTime: value })}
              />
            </Form.Field>
            <Form.Field required>
              <label>Horário Fim</label>
              <Input
                type="time"
                value={formData.endTime}
                onChange={(_, { value }) => setFormData({ ...formData, endTime: value })}
              />
            </Form.Field>
          </Form.Group>
          <Form.Field>
            <label>Dias Permitidos</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {Object.entries(DAY_NAMES).map(([key, name]) => {
                const day = parseInt(key);
                const isSelected = formData.allowedDays?.split(',').map(Number).includes(day);
                return (
                  <Checkbox
                    key={key}
                    label={name}
                    checked={isSelected}
                    onChange={() => toggleDay(day)}
                  />
                );
              })}
            </div>
          </Form.Field>
          <Form.Field>
            <Checkbox
              label="Regra Ativa"
              checked={formData.isActive}
              onChange={(_, { checked }) => setFormData({ ...formData, isActive: checked ?? true })}
            />
          </Form.Field>
          <Form.Field>
            <label>Descrição (opcional)</label>
            <Input
              placeholder="Ex: Horário padrão comercial"
              value={formData.description || ''}
              onChange={(_, { value }) => setFormData({ ...formData, description: value || null })}
            />
          </Form.Field>
        </Form>
      </BaseModal>
    </GlassPageWrapper>
  );
};

export default AccessManagementPage;
